/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import { ReactComponent as LeftArrow } from "../../assets/icons/arrows/left-arrow.svg";
import {
  FormInput,
  FormSelect,
  FormTwoInputs,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { useEffect, useMemo, useState } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useDrops } from "../../store/useDrops";
import { IMarketPricing, useMarketPricing } from "../../store/useMarketPricing";
import { useUsers } from "../../store/useUsers";
import { useAlert } from "../../store/useAlert";
import { useDropLocations } from "../../store/useDropLocations";
import { effectiveRate } from "../../utils/locationRates";

function AddDropScreen() {
  /** Hooks */
  const navigate = useNavigate();
  const params = useParams();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  const { createDrop, createdDrop, clearCreatedDrop } = useDrops();
  const { getAllUsers, getOneUser, oneUser, allUsers } = useUsers();
  const { setAlert } = useAlert();
  const { getDropLocations, dropLocations } = useDropLocations();
  /** State */
  const [submitted, setSubmitted] = useState(false);
  const [userId, setUserId] = useState<string>("");
  const [comment, setComment] = useState<string>("");
  const [dropCompositions, setDropCompositions] = useState<any[]>([]);
  const [totalPrice, setTotalPrice] = useState<any>(0);
  const [totalWeight, setTotalWeight] = useState<any>(0);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  useEffect(() => {
    getAllMarketPrices();
    getAllUsers();
    getDropLocations();
  }, []);

  // Preview only, in cents: the backend prices the drop itself, at the rate
  // of the selected location.
  const linePrice = (composition: IMarketPricing | undefined, weight: string) =>
    Number(
      (Number(weight) * effectiveRate(composition, selectedLocation) * 100).toFixed(2)
    );

  // Picking another location changes the rate, so reprice what was entered.
  useEffect(() => {
    if (!Object.keys(dropCompositions)?.length) return;
    const repriced: any = {};
    for (const id in dropCompositions) {
      const composition = marketPrices?.find((mp) => mp._id === id);
      repriced[id] = {
        ...dropCompositions[id],
        price: linePrice(composition, dropCompositions[id]?.weight),
      };
    }
    setDropCompositions(repriced);
  }, [selectedLocation]);

  useEffect(() => {
    if (!params?.userId) return;
    getOneUser(params?.userId);
  }, [params?.userId]);

  useEffect(() => {
    if (!Object.keys(dropCompositions)?.length) {
      return;
    }
    let finalTotalPrice = 0;
    let finalTotalWeight = 0;
    for (const key in dropCompositions) {
      finalTotalPrice += dropCompositions[key]?.price ?? 0;
      finalTotalWeight += Number(dropCompositions[key]?.weight) ?? 0;
    }
    setTotalPrice(String(finalTotalPrice / 100));
    // @ts-ignore
    setTotalWeight(String(parseFloat(finalTotalWeight ?? 0, 10) ?? 0));
  }, [dropCompositions]);

  const goBack = () => navigate(-1);

  function splitArray(arr: any[]) {
    const middle = Math.floor(arr.length / 2);
    return [arr.slice(0, middle + 1), arr.slice(middle + 1)];
  }

  const compositions = useMemo(() => {
    const activeMarketPrices =
      marketPrices?.filter((mp) => mp?.isActive !== false) ?? [];
    return splitArray(activeMarketPrices);
  }, [marketPrices]);

  useEffect(() => {
    if (createdDrop) {
      clearCreatedDrop();
      navigate(-1);
    }
  }, [createdDrop]);

  const onSubmit = () => {
    if ((userId === "" || !userId) && !params?.userId) {
      setSubmitted(true);
      setAlert({
        isOpen: true,
        message: "Please select a user.",
        severity: "error",
      });
      return;
    }
    if (!selectedLocation) {
      setSubmitted(true);
      setAlert({
        isOpen: true,
        message: "Please select a location.",
        severity: "error",
      });
      return;
    }
    if (Number(totalWeight) === 0) {
      return setAlert({
        isOpen: true,
        message: "Please add some weight.",
        severity: "error",
      });
    }

    const body = {
      user: params?.userId ?? userId,
      // totalPrice: Number(totalPrice) * 100,
      // totalWeight: Number(totalWeight),
      // comment: data?.comment,
      dropComposition: Object.entries(dropCompositions).map(([key, value]) => ({
        type: key,
        weight: Number(value?.weight),
        price: Number(value?.price),
      })),
      location: selectedLocation,
    };
    createDrop(body, comment);
  };

  const selectUser = (e: any) => setUserId(e?.target?.value);
  const handleComment = (e: any) => setComment(e?.target?.value);

  const userIdError = submitted && (userId === "" || !userId);

  return (
    <div>
      <div className="flex flex-row items-center mb-6 justify-between">
        <div className="flex flex-row items-center justify-start">
          <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            {params?.userId
              ? `Add new drop for ${oneUser?.first_name} ${oneUser?.last_name}`
              : "Add new drop"}
          </h1>
        </div>
      </div>
      <div className="w-full min-w-fit p-2 sm:p-7 bg-white rounded-xl shadow-none drop-shadow-none mb-[10px] sm:mb-[20px]">
        <div className="w-full bg-sky-lighter px-2 sm:px-10 py-1 sm:py-4 flex flex-row items-center justify-between font-satoshi text-dark font-medium text-base">
          <p>Weight</p>
          <p>Earnings</p>
        </div>
        <div className="w-full bg-white px-2 sm:px-10 py-1 sm:py-4 flex flex-row items-center justify-between font-satoshi text-dark font-normal text-base">
          <p>{totalWeight} kg</p>
          <p>{Number(totalPrice)?.toFixed(2)} €</p>
        </div>
      </div>
      <div className="w-full min-w-fit p-7 bg-white rounded-xl shadow-none drop-shadow-none mb-[10px] sm:mb-[20px]">
        {!params?.userId ? (
          <FormSelect
            label="Select user"
            placeholder="Select user"
            // @ts-ignore
            options={
              allUsers?.length
                ? allUsers?.map((user) => ({
                    value: user?._id,
                    label: `${user?.first_name} ${user?.last_name}`,
                  }))
                : []
            }
            error={userIdError}
            onChange={selectUser}
          />
        ) : null}
        <FormInput
          label="Comment"
          placeholder="Add comment"
          value={comment}
          onChange={handleComment}
        />
      </div>

      <div className="w-full min-w-fit p-7 bg-white rounded-xl shadow-none drop-shadow-none mb-[10px] sm:mb-[20px]">
        <FormSelect
          inputClassName="h-[38px] px-2 bg-sky-lighter text-sm"
          // @ts-ignore
          options={[
            // @ts-ignore
            ...(dropLocations?.map((location) => ({
              value: location?._id as string,
              label: location?.cost_place as string,
            })) ?? []),
          ]}
          value={selectedLocation as string}
          onChange={(e) => {
            setSelectedLocation(e.target.value);
          }}
          error={submitted && !selectedLocation}
          label="Select location"
          placeholder="Change location here..."
        />
      </div>
      <div className="w-full flex flex-col sm:flex-row mb-6">
        {compositions?.[0]?.length ? (
          <div className="w-full sm:w-1/2 min-w-fit p-7 bg-white rounded-xl shadow-none drop-shadow-none sm:mr-2 mb-1 sm:mb-0">
            {compositions?.[0]?.map((composition: IMarketPricing) => {
              return (
                <FormTwoInputs
                  rightValue={
                    String(
                      // @ts-ignore
                      dropCompositions?.[composition?._id]?.weight
                        ? // @ts-ignore
                          (dropCompositions?.[composition?._id]?.price ?? 0) / 100
                        : ""
                    ) ?? ""
                  }
                  leftValue={
                    // @ts-ignore
                    dropCompositions?.[composition?._id]?.weight ?? ""
                  }
                  key={composition?._id}
                  label={composition?.name}
                  leftPlaceholder="Weight"
                  rightPlaceholder="€"
                  rightDisabled
                  leftOnChange={(e) => {
                    const newValue = e?.target?.value?.replaceAll(",", ".");
                    const value = Number(newValue);
                    if (isNaN(value)) return;
                    setDropCompositions({
                      ...dropCompositions,
                      // @ts-ignore
                      [composition?._id]: {
                        // @ts-ignore
                        ...(dropCompositions?.[composition?._id] ?? {}),
                        weight: newValue,
                        price: linePrice(composition, newValue),
                      },
                    });
                  }}
                />
              );
            })}
          </div>
        ) : null}

        {compositions?.[1]?.length ? (
          <div className="w-full sm:w-1/2 min-w-fit p-7 bg-white rounded-xl shadow-none drop-shadow-none mr-2">
            {compositions?.[1]?.map((composition: IMarketPricing) => {
              return (
                <FormTwoInputs
                  rightValue={
                    String(
                      // @ts-ignore
                      dropCompositions?.[composition?._id]?.weight
                        ? // @ts-ignore
                          (dropCompositions?.[composition?._id]?.price ?? 0) / 100
                        : ""
                    ) ?? ""
                  }
                  leftValue={
                    // @ts-ignore
                    dropCompositions?.[composition?._id]?.weight ?? ""
                  }
                  key={composition?._id}
                  label={composition?.name}
                  leftPlaceholder="Weight"
                  rightPlaceholder="€"
                  rightDisabled
                  leftOnChange={(e) => {
                    const newValue = e?.target?.value?.replaceAll(",", ".");
                    const value = Number(newValue);
                    if (isNaN(value)) return;
                    setDropCompositions({
                      ...dropCompositions,
                      // @ts-ignore
                      [composition?._id]: {
                        // @ts-ignore
                        ...(dropCompositions?.[composition?._id] ?? {}),
                        weight: newValue,
                        price: linePrice(composition, newValue),
                      },
                    });
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </div>
      <div className="flex flex-row items-center justify-end">
        <SecondaryButton
          onClick={goBack}
          label="Cancel"
          className="h-8 px-4 py-0 bg-white border border-droppie-oranje text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
          labelClassName="text-droppie-oranje"
        />
        <PrimaryButton
          onClick={onSubmit}
          className="h-8 ml-4 px-4 py-0 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          size="sm"
          label="Save"
        />
      </div>
    </div>
  );
}

export default withProtectedRoute(AddDropScreen);
